/**
 * API Utilities
 * HTTP client functions for interacting with the TopoloCRM API
 */

import type {
  ApiResponse,
  Contact,
  ContactFormData,
  Note,
  NoteFormData,
  Attachment,
  PipelineBoard,
  Pipeline,
  Stage,
  Company,
  Deal,
  ContactWithStage,
  ContactsResponse,
  NotesResponse,
  AttachmentsResponse,
  PipelineHistoryResponse,
  SearchParams,
  TaskItem,
  TasksResponse,
  LeadAssignmentRule,
  LeadRoutingRulesResponse,
  SavedView,
  SavedViewsResponse,
  CoreDashboardResponse,
  OrgRoleDefault,
  Ticket,
  TicketDetailResponse,
  TicketMessage,
  TicketsResponse,
  OrgSettingsResponse,
  OrgModuleSettings,
  CustomFieldChanges,
  CustomFieldDefinition,
  CustomFieldDefinitionInput,
  CustomFieldObjectType,
  CustomFieldsResponse,
  CustomObjectDefinition,
  CustomObjectInput,
  CustomObjectsResponse,
  ObjectRecord,
  ObjectRecordsParams,
  ObjectRecordsResponse,
  CrmPacksResponse,
} from '../types/api';
import { api, API_ERRORS, getUploadAuthHeaders, uploadFetch } from './api/api-client';
export {
  portalSyncApi,
  realEstateMediaApi,
  portalConnectionsApi,
  portalLeadsApi,
} from './api/real-estate-api';
// Contact API functions
export const contactsApi = {
  // Get paginated contacts list
  async getContacts(params: SearchParams = {}): Promise<ApiResponse<ContactsResponse>> {
    const searchParams = new URLSearchParams();

    if (params.page) searchParams.set('page', params.page.toString());
    if (params.limit) searchParams.set('limit', params.limit.toString());
    if (params.search) searchParams.set('q', params.search);
    if (params.sort_by) searchParams.set('sort_by', params.sort_by);
    if (params.sort_order) searchParams.set('sort_order', params.sort_order);

    // Add filters
    if (params.filters) {
      Object.entries(params.filters).forEach(([key, value]) => {
        if (value) searchParams.set(key, value);
      });
    }

    // Add all other parameters (including advanced filters) directly
    Object.entries(params).forEach(([key, value]) => {
      if (
        key !== 'page' &&
        key !== 'limit' &&
        key !== 'search' &&
        key !== 'sort_by' &&
        key !== 'sort_order' &&
        key !== 'filters'
      ) {
        if (value !== undefined && value !== '' && value !== null) {
          searchParams.set(key, String(value));
        }
      }
    });

    const queryString = searchParams.toString();
    const endpoint = `/contacts${queryString ? `?${queryString}` : ''}`;

    return api.get<ContactsResponse>(endpoint);
  },

  // Get single contact
  async getContact(id: string): Promise<ApiResponse<Contact>> {
    return api.get<Contact>(`/contacts/${id}`);
  },

  // Create contact
  async createContact(data: ContactFormData): Promise<ApiResponse<Contact>> {
    return api.post<Contact>('/contacts', data);
  },

  // Update contact
  async updateContact(id: string, data: Partial<ContactFormData>): Promise<ApiResponse<Contact>> {
    return api.put<Contact>(`/contacts/${id}`, data);
  },

  // Delete contact
  async deleteContact(id: string): Promise<ApiResponse<void>> {
    return api.delete<void>(`/contacts/${id}`);
  },
};

// Notes API functions
export const notesApi = {
  // Get notes for a contact
  async getNotes(contactId: string): Promise<ApiResponse<NotesResponse>> {
    return api.get<NotesResponse>(`/contacts/${contactId}/notes`);
  },

  // Create note
  async createNote(contactId: string, data: NoteFormData): Promise<ApiResponse<Note>> {
    return api.post<Note>(`/contacts/${contactId}/notes`, data);
  },

  // Update note
  async updateNote(
    contactId: string,
    noteId: string,
    data: Partial<NoteFormData>
  ): Promise<ApiResponse<Note>> {
    return api.put<Note>(`/contacts/${contactId}/notes/${noteId}`, data);
  },

  // Delete note
  async deleteNote(contactId: string, noteId: string): Promise<ApiResponse<void>> {
    return api.delete<void>(`/contacts/${contactId}/notes/${noteId}`);
  },
};

// Attachments API functions
export const attachmentsApi = {
  // Get attachments for a contact
  async getAttachments(contactId: string): Promise<ApiResponse<AttachmentsResponse>> {
    return api.get<AttachmentsResponse>(`/contacts/${contactId}/attachments`);
  },

  // Upload attachment
  async uploadAttachment(contactId: string, file: File): Promise<ApiResponse<Attachment>> {
    const formData = new FormData();
    formData.append('file', file);

    const url = `${api.getBaseUrl()}/contacts/${contactId}/attachments`;

    try {
      const response = await uploadFetch(url, {
        method: 'POST',
        headers: getUploadAuthHeaders(),
        body: formData,
      });

      const data = await response.json();

      if (!response.ok) {
        return {
          success: false,
          error: data.error || API_ERRORS.uploadFailed,
          code: response.status,
        };
      }

      return {
        success: true,
        data,
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : API_ERRORS.uploadFailed,
      };
    }
  },

  // Delete attachment
  async deleteAttachment(contactId: string, attachmentId: string): Promise<ApiResponse<void>> {
    return api.delete<void>(`/contacts/${contactId}/attachments/${attachmentId}`);
  },

  // Download attachment
  getDownloadUrl(contactId: string, attachmentId: string): string {
    return `${api.getBaseUrl()}/contacts/${contactId}/attachments/${attachmentId}/download`;
  },
};

// Pipeline API functions
export const pipelineApi = {
  // Get pipeline structure with stage counts (no contacts)
  async getPipelineStructure(pipelineId: string): Promise<ApiResponse<PipelineBoard>> {
    return api.get<PipelineBoard>(`/pipeline/${pipelineId}/structure`);
  },

  // Get pipeline board with contacts grouped by stage
  async getPipelineBoard(
    pipelineId: string,
    page = 1,
    limit = 50
  ): Promise<ApiResponse<PipelineBoard>> {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
    });
    return api.get<PipelineBoard>(`/pipeline/${pipelineId}/board?${params}`);
  },

  // Get all pipelines
  async getPipelines(options?: {
    signal?: AbortSignal;
    resourceHeaders?: Record<string, string>;
  }): Promise<ApiResponse<Pipeline[]>> {
    return api.get<Pipeline[]>('/pipeline', {
      signal: options?.signal,
      headers: options?.resourceHeaders,
    });
  },

  // Get pipeline details
  async getPipeline(pipelineId: string): Promise<ApiResponse<Pipeline & { stages: Stage[] }>> {
    return api.get<Pipeline & { stages: Stage[] }>(`/pipeline/${pipelineId}`);
  },

  // Move contact to different stage
  async moveContact(
    contactId: string,
    stageId: string
  ): Promise<
    ApiResponse<{
      message: string;
      contact: ContactWithStage;
      previousStage: { id: string; name: string; position: number } | null;
      newStage: { id: string; name: string; position: number };
    }>
  > {
    return api.put(`/pipeline/${contactId}/move`, { stageId });
  },

  // Get pipeline history for a contact
  async getPipelineHistory(contactId: string): Promise<ApiResponse<PipelineHistoryResponse>> {
    return api.get<PipelineHistoryResponse>(`/pipeline/${contactId}/history`);
  },

  // Add contact to pipeline stage
  async addContactToStage(
    contactId: string,
    stageId: string
  ): Promise<
    ApiResponse<{ message: string; contactId: string; stageId: string; stageName: string }>
  > {
    return api.post(`/pipeline/${contactId}/add`, { stageId });
  },

  // Remove contact from pipeline
  async removeContactFromPipeline(
    contactId: string
  ): Promise<ApiResponse<{ message: string; contactId: string }>> {
    return api.delete(`/pipeline/${contactId}/remove`);
  },

  // Check if contact is in pipeline
  async getContactPipelineStatus(contactId: string): Promise<
    ApiResponse<{
      inPipeline: boolean;
      stageId?: string;
      stageName?: string;
      stageColor?: string;
      pipelineId?: string;
      pipelineName?: string;
    }>
  > {
    return api.get(`/pipeline/${contactId}/status`);
  },

  // Get pipeline stages
  async getPipelineStages(pipelineId: string): Promise<
    ApiResponse<
      Array<{
        id: string;
        name: string;
        position: number;
        color: string;
      }>
    >
  > {
    return api.get(`/pipeline/${pipelineId}/stages`);
  },

  // Update stage properties (color, name)
  async updateStage(
    stageId: string,
    updates: { color?: string; name?: string }
  ): Promise<
    ApiResponse<{
      message: string;
      stage: {
        id: string;
        pipeline_id: string;
        name: string;
        position: number;
        color: string;
        created_at: number;
        updated_at: number;
      };
    }>
  > {
    return api.put(`/pipeline/stages/${stageId}`, updates);
  },
};

// Health check
// Companies API functions
export const companiesApi = {
  // Get companies list
  async getCompanies(params: { search?: string; page?: number; limit?: number } = {}): Promise<
    ApiResponse<{
      companies: Array<Company & { contact_count: number }>;
      pagination: {
        page: number;
        limit: number;
        total: number;
        pages: number;
      };
    }>
  > {
    const searchParams = new URLSearchParams();

    if (params.search) searchParams.set('search', params.search);
    if (params.page) searchParams.set('page', params.page.toString());
    if (params.limit) searchParams.set('limit', params.limit.toString());

    const queryString = searchParams.toString();
    const endpoint = `/companies${queryString ? `?${queryString}` : ''}`;

    return api.get(endpoint);
  },

  // Get single company
  async getCompany(id: string): Promise<ApiResponse<{ company: Company }>> {
    return api.get(`/companies/${id}`);
  },

  // Create company
  async createCompany(data: {
    name: string;
    description?: string;
    website?: string;
    domain?: string;
    phone?: string;
    fax?: string;
    email?: string;
    address?: string;
    city?: string;
    state?: string;
    postal_code?: string;
    country?: string;
    industry?: string;
    size?: 'startup' | 'small' | 'medium' | 'large' | 'enterprise';
    founded_year?: number;
    ticker?: string;
    employee_count?: number;
    employee_range?: string;
    revenue_range?: string;
    primary_sub_industry?: string;
    linkedin_url?: string;
    external_id?: string;
    external_profile_url?: string;
    logo_url?: string;
    custom_fields?: CustomFieldChanges;
  }): Promise<ApiResponse<Company>> {
    return api.post('/companies', data);
  },

  // Update company
  async updateCompany(
    id: string,
    data: {
      name?: string;
      description?: string;
      website?: string;
      domain?: string;
      phone?: string;
      fax?: string;
      email?: string;
      address?: string;
      city?: string;
      state?: string;
      postal_code?: string;
      country?: string;
      industry?: string;
      size?: 'startup' | 'small' | 'medium' | 'large' | 'enterprise';
      founded_year?: number;
      ticker?: string;
      employee_count?: number;
      employee_range?: string;
      revenue_range?: string;
      primary_sub_industry?: string;
      linkedin_url?: string;
      external_id?: string;
      external_profile_url?: string;
      logo_url?: string;
      custom_fields?: CustomFieldChanges;
    }
  ): Promise<ApiResponse<Company>> {
    return api.put(`/companies/${id}`, data);
  },

  // Delete company
  async deleteCompany(id: string): Promise<ApiResponse<{ message: string }>> {
    return api.delete(`/companies/${id}`);
  },
};

export const tasksApi = {
  async getTasks(
    params: {
      search?: string;
      page?: number;
      limit?: number;
      status?: string;
      priority?: string;
      entity_type?: string;
      entity_id?: string;
      owner_id?: string;
    } = {}
  ): Promise<ApiResponse<TasksResponse>> {
    const searchParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        searchParams.set(key, String(value));
      }
    });
    const endpoint = searchParams.toString() ? `/tasks?${searchParams.toString()}` : '/tasks';
    const response = await api.get<{ success: boolean; data: TasksResponse }>(endpoint);
    return {
      success: response.success,
      data: response.data?.data,
      error: response.error,
      code: response.code,
    };
  },

  async createTask(data: Partial<TaskItem>): Promise<ApiResponse<{ task_item: TaskItem }>> {
    const response = await api.post<{ success: boolean; data: { task_item: TaskItem } }>(
      '/tasks',
      data
    );
    return {
      success: response.success,
      data: response.data?.data,
      error: response.error,
      code: response.code,
    };
  },

  async updateTask(
    taskId: string,
    data: Partial<TaskItem>
  ): Promise<ApiResponse<{ task_item: TaskItem }>> {
    const response = await api.put<{ success: boolean; data: { task_item: TaskItem } }>(
      `/tasks/${taskId}`,
      data
    );
    return {
      success: response.success,
      data: response.data?.data,
      error: response.error,
      code: response.code,
    };
  },

  async deleteTask(taskId: string): Promise<ApiResponse<{ message: string }>> {
    const response = await api.delete<{ success: boolean; data: { message: string } }>(
      `/tasks/${taskId}`
    );
    return {
      success: response.success,
      data: response.data?.data,
      error: response.error,
      code: response.code,
    };
  },
};

export const leadRoutingApi = {
  async getRules(): Promise<ApiResponse<LeadRoutingRulesResponse>> {
    const response = await api.get<{ success: boolean; data: LeadRoutingRulesResponse }>(
      '/lead-routing/rules'
    );
    return {
      success: response.success,
      data: response.data?.data,
      error: response.error,
      code: response.code,
    };
  },

  async createRule(
    data: Partial<LeadAssignmentRule>
  ): Promise<ApiResponse<{ rule: LeadAssignmentRule }>> {
    const response = await api.post<{ success: boolean; data: { rule: LeadAssignmentRule } }>(
      '/lead-routing/rules',
      data
    );
    return {
      success: response.success,
      data: response.data?.data,
      error: response.error,
      code: response.code,
    };
  },

  async updateRule(
    ruleId: string,
    data: Partial<LeadAssignmentRule>
  ): Promise<ApiResponse<{ rule: LeadAssignmentRule }>> {
    const response = await api.put<{ success: boolean; data: { rule: LeadAssignmentRule } }>(
      `/lead-routing/rules/${ruleId}`,
      data
    );
    return {
      success: response.success,
      data: response.data?.data,
      error: response.error,
      code: response.code,
    };
  },
};

export const ticketsApi = {
  async getTickets(
    params: {
      status?: string;
      assignee_id?: string;
      contact_id?: string;
      company_id?: string;
      source?: string;
      external_thread_id?: string;
      limit?: number;
    } = {}
  ): Promise<ApiResponse<TicketsResponse>> {
    const searchParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        searchParams.set(key, String(value));
      }
    });
    const endpoint = searchParams.toString() ? `/tickets?${searchParams.toString()}` : '/tickets';
    const response = await api.get<TicketsResponse>(endpoint);
    return {
      success: response.success,
      data: response.data,
      error: response.error,
      code: response.code,
    };
  },

  async getTicket(ticketId: string): Promise<ApiResponse<TicketDetailResponse>> {
    const response = await api.get<TicketDetailResponse>(`/tickets/${ticketId}`);
    return {
      success: response.success,
      data: response.data,
      error: response.error,
      code: response.code,
    };
  },

  async createTicket(data: {
    subject: string;
    status?: string;
    priority?: string;
    contact_id?: string;
    company_id?: string;
    assignee_id?: string;
    source?: string;
    external_thread_id?: string;
    message?: {
      body: string;
      direction?: 'inbound' | 'outbound';
      sender?: string;
      recipients?: string;
      message_id?: string;
      in_reply_to?: string;
    };
  }): Promise<ApiResponse<{ ticket: Ticket }>> {
    const response = await api.post<{ ticket: Ticket }>('/tickets', data);
    return {
      success: response.success,
      data: response.data,
      error: response.error,
      code: response.code,
    };
  },

  async addMessage(
    ticketId: string,
    data: {
      body: string;
      direction?: 'inbound' | 'outbound';
      sender?: string;
      recipients?: string;
      message_id?: string;
      in_reply_to?: string;
    }
  ): Promise<ApiResponse<{ message: TicketMessage }>> {
    const response = await api.post<{ message: TicketMessage }>(
      `/tickets/${ticketId}/messages`,
      data
    );
    return {
      success: response.success,
      data: response.data,
      error: response.error,
      code: response.code,
    };
  },
};

// Import/Export API functions
export const importExportApi = {
  // Preview import data
  async previewImport(file: File): Promise<
    ApiResponse<{
      headers: string[];
      sampleRows: string[][];
      totalRows: number;
      suggestedMapping: Record<string, string>;
    }>
  > {
    try {
      // Read file content as text
      const csvData = await file.text();

      // Send as JSON to match backend expectation
      const response = await api.post<{
        headers: string[];
        sampleRows: string[][];
        totalRows: number;
        suggestedMapping: Record<string, string>;
      }>('/import-export/preview', {
        csvData,
        delimiter: ',',
      });

      return response;
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : API_ERRORS.readFileFailed,
      };
    }
  },

  // Execute import
  async executeImport(
    file: File,
    options: {
      columnMapping?: Record<string, string>;
      delimiter?: string;
      createCompanies?: boolean;
      fileName?: string;
      storeSourceRows?: boolean;
    } = {}
  ): Promise<
    ApiResponse<{
      success: boolean;
      imported: number;
      failed: number;
      companiesCreated: number;
      errors: Array<{ row: number; error: string }>;
    }>
  > {
    try {
      // Read file content as text
      const csvData = await file.text();

      // Send as JSON to match backend expectation
      const response = await api.post<{
        success: boolean;
        imported: number;
        failed: number;
        companiesCreated: number;
        errors: Array<{ row: number; error: string }>;
      }>('/import-export/execute', {
        csvData,
        columnMapping: options.columnMapping || {},
        delimiter: options.delimiter || ',',
        createCompanies: options.createCompanies !== false,
        fileName: options.fileName || file.name,
        storeSourceRows: options.storeSourceRows !== false,
      });

      return response;
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : API_ERRORS.readFileFailed,
      };
    }
  },

  // Download attachment by ID
  async downloadAttachment(
    attachmentId: string
  ): Promise<ApiResponse<{ blob: Blob; filename: string }>> {
    // getUploadAuthHeaders carries the workspace resource header so the read is
    // scoped to the active workspace (attachments are workspace-scoped rows).
    const headers = getUploadAuthHeaders();
    try {
      const response = await uploadFetch(`${api.getBaseUrl()}/attachments/${attachmentId}`, {
        method: 'GET',
        headers,
      });
      if (!response.ok) {
        if (response.status === 401) window.dispatchEvent(new CustomEvent('auth:refresh-needed'));
        return {
          success: false,
          error: `Download failed (${response.status})`,
          code: response.status,
        };
      }
      const disposition = response.headers.get('content-disposition') || '';
      const match = disposition.match(/filename="([^"]+)"/);
      const filename = match ? match[1] : attachmentId;
      const blob = await response.blob();
      return { success: true, data: { blob, filename } };
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : API_ERRORS.network };
    }
  },

  // Download import template
  async downloadTemplate(): Promise<ApiResponse<Blob>> {
    // getUploadAuthHeaders carries auth + the workspace resource header.
    const headers = getUploadAuthHeaders();

    try {
      const response = await uploadFetch(`${api.getBaseUrl()}/import-export/template`, {
        method: 'GET',
        headers,
      });

      if (!response.ok) {
        if (response.status === 401) {
          window.dispatchEvent(new CustomEvent('auth:refresh-needed'));
        }
        const errorData = await response.json();
        return {
          success: false,
          error: errorData.error || API_ERRORS.downloadTemplateFailed,
          code: response.status,
        };
      }

      const blob = await response.blob();
      return {
        success: true,
        data: blob,
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : API_ERRORS.network,
      };
    }
  },
};

export const healthApi = {
  async checkHealth(): Promise<ApiResponse<{ status: string; timestamp: string }>> {
    return api.get<{ status: string; timestamp: string }>('/health');
  },
};

export const settingsApi = {
  async getOrgSettings(): Promise<ApiResponse<OrgSettingsResponse>> {
    const response = await api.get<{ success: boolean; data: OrgSettingsResponse }>(
      '/settings/org'
    );
    return {
      success: response.success,
      data: response.data?.data,
      error: response.error,
      code: response.code,
    };
  },

  async updateOrgSettings(payload: {
    module_settings: OrgModuleSettings;
    role_defaults?: OrgRoleDefault[];
  }): Promise<ApiResponse<OrgSettingsResponse>> {
    const response = await api.put<{ success: boolean; data: OrgSettingsResponse }>(
      '/settings/org',
      payload
    );
    return {
      success: response.success,
      data: response.data?.data,
      error: response.error,
      code: response.code,
    };
  },
};

function unwrapCustomFieldResponse<T>(response: {
  success: boolean;
  data?: { success: boolean; data: T };
  error?: string;
  code?: number;
}): ApiResponse<T> {
  return {
    success: response.success,
    data: response.data?.data,
    error: response.error,
    code: response.code,
  };
}

export const customFieldsApi = {
  async listCustomFields(
    objectType?: CustomFieldObjectType
  ): Promise<ApiResponse<CustomFieldsResponse>> {
    return unwrapCustomFieldResponse(
      await api.get<{ success: boolean; data: CustomFieldsResponse }>(
        `/custom-fields${objectType ? `?object_type=${objectType}` : ''}`
      )
    );
  },

  async createCustomField(
    payload: CustomFieldDefinitionInput
  ): Promise<ApiResponse<{ custom_field: CustomFieldDefinition }>> {
    return unwrapCustomFieldResponse(
      await api.post<{ success: boolean; data: { custom_field: CustomFieldDefinition } }>(
        '/custom-fields',
        payload
      )
    );
  },

  async updateCustomField(
    id: string,
    payload: CustomFieldDefinitionInput
  ): Promise<ApiResponse<{ custom_field: CustomFieldDefinition }>> {
    return unwrapCustomFieldResponse(
      await api.put<{ success: boolean; data: { custom_field: CustomFieldDefinition } }>(
        `/custom-fields/${id}`,
        payload
      )
    );
  },

  async deleteCustomField(id: string): Promise<ApiResponse<{ deleted: boolean; id: string }>> {
    return unwrapCustomFieldResponse(
      await api.delete<{ success: boolean; data: { deleted: boolean; id: string } }>(
        `/custom-fields/${id}`
      )
    );
  },
};

export const customObjectsApi = {
  async listCustomObjects(): Promise<ApiResponse<CustomObjectsResponse>> {
    return unwrapCustomFieldResponse(
      await api.get<{ success: boolean; data: CustomObjectsResponse }>('/custom-objects')
    );
  },

  async createCustomObject(
    payload: CustomObjectInput
  ): Promise<ApiResponse<{ custom_object: CustomObjectDefinition }>> {
    return unwrapCustomFieldResponse(
      await api.post<{ success: boolean; data: { custom_object: CustomObjectDefinition } }>(
        '/custom-objects',
        payload
      )
    );
  },

  async updateCustomObject(
    key: string,
    payload: CustomObjectInput
  ): Promise<ApiResponse<{ custom_object: CustomObjectDefinition }>> {
    return unwrapCustomFieldResponse(
      await api.put<{ success: boolean; data: { custom_object: CustomObjectDefinition } }>(
        `/custom-objects/${key}`,
        payload
      )
    );
  },

  async deleteCustomObject(key: string): Promise<ApiResponse<{ deleted: boolean; key: string }>> {
    return unwrapCustomFieldResponse(
      await api.delete<{ success: boolean; data: { deleted: boolean; key: string } }>(
        `/custom-objects/${key}`
      )
    );
  },
};

export const objectRecordsApi = {
  async listRecords(
    objectKey: string,
    params: ObjectRecordsParams = {}
  ): Promise<ApiResponse<ObjectRecordsResponse>> {
    const query = new URLSearchParams();
    if (params.q) query.set('q', params.q);
    if (params.page) query.set('page', String(params.page));
    if (params.limit) query.set('limit', String(params.limit));
    if (params.sort_by) query.set('sort_by', params.sort_by);
    if (params.sort_order) query.set('sort_order', params.sort_order);
    for (const [key, value] of Object.entries(params.filters || {})) {
      if (value) query.set(`cf.${key}`, value);
    }
    const suffix = query.toString();
    return unwrapCustomFieldResponse(
      await api.get<{ success: boolean; data: ObjectRecordsResponse }>(
        `/objects/${objectKey}/records${suffix ? `?${suffix}` : ''}`
      )
    );
  },

  async getRecord(objectKey: string, id: string): Promise<ApiResponse<{ record: ObjectRecord }>> {
    return unwrapCustomFieldResponse(
      await api.get<{ success: boolean; data: { record: ObjectRecord } }>(
        `/objects/${objectKey}/records/${id}`
      )
    );
  },

  async createRecord(
    objectKey: string,
    payload: { name: string; owner_id?: string | null; custom_fields?: CustomFieldChanges }
  ): Promise<ApiResponse<{ record: ObjectRecord }>> {
    return unwrapCustomFieldResponse(
      await api.post<{ success: boolean; data: { record: ObjectRecord } }>(
        `/objects/${objectKey}/records`,
        payload
      )
    );
  },

  async updateRecord(
    objectKey: string,
    id: string,
    payload: { name?: string; owner_id?: string | null; custom_fields?: CustomFieldChanges }
  ): Promise<ApiResponse<{ record: ObjectRecord }>> {
    return unwrapCustomFieldResponse(
      await api.put<{ success: boolean; data: { record: ObjectRecord } }>(
        `/objects/${objectKey}/records/${id}`,
        payload
      )
    );
  },

  async deleteRecord(
    objectKey: string,
    id: string
  ): Promise<ApiResponse<{ deleted: boolean; id: string }>> {
    return unwrapCustomFieldResponse(
      await api.delete<{ success: boolean; data: { deleted: boolean; id: string } }>(
        `/objects/${objectKey}/records/${id}`
      )
    );
  },
};

export interface RecordFile {
  id: string;
  filename: string;
  mime_type: string;
  size: number;
  created_at: number;
}

/** Files referenced by file custom fields. */
export const filesApi = {
  async upload(file: File): Promise<ApiResponse<{ file: RecordFile }>> {
    const formData = new FormData();
    formData.append('file', file);
    try {
      const response = await uploadFetch(`${api.getBaseUrl()}/files`, {
        method: 'POST',
        headers: getUploadAuthHeaders(),
        body: formData,
      });
      const body = (await response.json()) as {
        data?: { file: RecordFile };
        error?: { message?: string };
      };
      if (!response.ok || !body.data) {
        return {
          success: false,
          error: body.error?.message || API_ERRORS.uploadFailed,
          code: response.status,
        };
      }
      return { success: true, data: body.data };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : API_ERRORS.uploadFailed,
      };
    }
  },

  async download(fileId: string): Promise<ApiResponse<{ blob: Blob; filename: string }>> {
    try {
      const response = await uploadFetch(`${api.getBaseUrl()}/files/${fileId}`, {
        method: 'GET',
        headers: getUploadAuthHeaders(),
      });
      if (!response.ok) {
        if (response.status === 401) window.dispatchEvent(new CustomEvent('auth:refresh-needed'));
        return {
          success: false,
          error: `Download failed (${response.status})`,
          code: response.status,
        };
      }
      const match = (response.headers.get('Content-Disposition') || '').match(/filename="([^"]+)"/);
      return {
        success: true,
        data: { blob: await response.blob(), filename: match ? match[1] : fileId },
      };
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : API_ERRORS.network };
    }
  },
};

export const packsApi = {
  async listPacks(): Promise<ApiResponse<CrmPacksResponse>> {
    return unwrapCustomFieldResponse(
      await api.get<{ success: boolean; data: CrmPacksResponse }>('/packs')
    );
  },

  async installPack(
    key: string,
    options: { include_sample_data: boolean }
  ): Promise<ApiResponse<{ pack_key: string; version: number }>> {
    return unwrapCustomFieldResponse(
      await api.post<{ success: boolean; data: { pack_key: string; version: number } }>(
        `/packs/${key}/install`,
        options
      )
    );
  },

  async upgradePack(key: string): Promise<ApiResponse<{ pack_key: string; version: number }>> {
    return unwrapCustomFieldResponse(
      await api.post<{ success: boolean; data: { pack_key: string; version: number } }>(
        `/packs/${key}/upgrade`,
        {}
      )
    );
  },

  async uninstallPack(
    key: string,
    options: { delete_data: boolean }
  ): Promise<ApiResponse<{ pack_key: string; uninstalled: boolean }>> {
    return unwrapCustomFieldResponse(
      await api.post<{ success: boolean; data: { pack_key: string; uninstalled: boolean } }>(
        `/packs/${key}/uninstall`,
        options
      )
    );
  },
};

export const dashboardApi = {
  async getCoreDashboard(): Promise<ApiResponse<CoreDashboardResponse>> {
    const response = await api.get<{ success: boolean; data: CoreDashboardResponse }>(
      '/dashboard/core'
    );
    return {
      success: response.success,
      data: response.data?.data,
      error: response.error,
      code: response.code,
    };
  },
};

export const savedViewsApi = {
  async getSavedViews(moduleKey: string): Promise<ApiResponse<SavedViewsResponse>> {
    const response = await api.get<{ success: boolean; data: SavedViewsResponse }>(
      `/saved-views?module_key=${encodeURIComponent(moduleKey)}`
    );
    return {
      success: response.success,
      data: response.data?.data,
      error: response.error,
      code: response.code,
    };
  },

  async createSavedView(payload: {
    module_key: string;
    name: string;
    config: Record<string, unknown>;
  }): Promise<ApiResponse<{ saved_view: SavedView | null }>> {
    const response = await api.post<{ success: boolean; data: { saved_view: SavedView | null } }>(
      '/saved-views',
      payload
    );
    return {
      success: response.success,
      data: response.data?.data,
      error: response.error,
      code: response.code,
    };
  },

  async updateSavedView(
    savedViewId: string,
    payload: {
      name?: string;
      config?: Record<string, unknown>;
    }
  ): Promise<ApiResponse<{ saved_view: SavedView | null }>> {
    const response = await api.put<{ success: boolean; data: { saved_view: SavedView | null } }>(
      `/saved-views/${savedViewId}`,
      payload
    );
    return {
      success: response.success,
      data: response.data?.data,
      error: response.error,
      code: response.code,
    };
  },

  async deleteSavedView(savedViewId: string): Promise<ApiResponse<{ id: string }>> {
    const response = await api.delete<{ success: boolean; data: { id: string } }>(
      `/saved-views/${savedViewId}`
    );
    return {
      success: response.success,
      data: response.data?.data,
      error: response.error,
      code: response.code,
    };
  },
};

// ====================================
// GLOBAL SEARCH API
// ====================================

export interface GlobalSearchResult {
  id: string;
  type: 'contact' | 'company' | 'deal';
  title: string;
  subtitle?: string;
  description?: string;
  url: string;
  metadata?: Record<string, unknown>;
}

export interface GlobalSearchResponse {
  results: GlobalSearchResult[];
  total: number;
  query: string;
}

export const globalSearchApi = {
  async search(query: string, limit = 20): Promise<ApiResponse<GlobalSearchResponse>> {
    const searchParams = new URLSearchParams();
    searchParams.set('q', query);
    searchParams.set('limit', limit.toString());

    return api.get<GlobalSearchResponse>(`/search?${searchParams.toString()}`);
  },
};

// ====================================
// DEALS API
// ====================================

export type DealsSearchParams = Omit<SearchParams, 'sort_by'> & {
  sort_by?: 'title' | 'amount_cents' | 'stage' | 'created_at';
  stage_id?: string;
  pipeline_id?: string;
  owner_id?: string;
  company_id?: string;
  contact_id?: string;
  min_amount?: number;
  max_amount?: number;
  currency?: string;
  min_probability?: number;
  max_probability?: number;
  expected_close_after?: string;
  expected_close_before?: string;
  created_after?: string;
  created_before?: string;
  updated_after?: string;
  updated_before?: string;
};

export const dealsApi = {
  // Get deals list with filtering and pagination
  async getDeals(params: DealsSearchParams = {}): Promise<
    ApiResponse<{
      deals: Deal[];
      pagination: {
        page: number;
        limit: number;
        total: number;
        totalPages: number;
        hasMore: boolean;
      };
      // Echoed-back filter params; never read by callers — left untyped intentionally.
      filters: unknown;
      sorting: { sort_by: string; sort_order: string };
    }>
  > {
    const searchParams = new URLSearchParams();

    if (params.page) searchParams.set('page', params.page.toString());
    if (params.limit) searchParams.set('limit', params.limit.toString());
    if (params.search) searchParams.set('search', params.search);
    if (params.sort_by) searchParams.set('sort_by', params.sort_by);
    if (params.sort_order) searchParams.set('sort_order', params.sort_order);

    // Add deal-specific filters
    Object.entries(params).forEach(([key, value]) => {
      if (
        key !== 'page' &&
        key !== 'limit' &&
        key !== 'search' &&
        key !== 'sort_by' &&
        key !== 'sort_order'
      ) {
        if (value !== undefined && value !== '' && value !== null) {
          searchParams.set(key, value.toString());
        }
      }
    });

    const queryString = searchParams.toString();
    const endpoint = queryString ? `/deals?${queryString}` : '/deals';

    return api.get(endpoint);
  },

  // Get single deal
  async getDeal(dealId: string): Promise<ApiResponse<Deal>> {
    return api.get(`/deals/${dealId}`);
  },

  // Create new deal
  async createDeal(data: {
    name: string;
    description?: string;
    owner_id?: string;
    company_id?: string;
    contact_id?: string;
    amount_cents?: number;
    currency?: string;
    probability?: number;
    expected_close_date?: number;
    pipeline_id?: string;
    stage_id?: string;
    custom_fields?: CustomFieldChanges;
  }): Promise<ApiResponse<Deal>> {
    return api.post('/deals', data);
  },

  // Update deal
  async updateDeal(
    dealId: string,
    data: Partial<{
      name: string;
      description: string;
      owner_id: string;
      company_id: string;
      contact_id: string;
      amount_cents: number;
      currency: string;
      probability: number;
      expected_close_date: number;
      pipeline_id: string;
      stage_id: string;
      custom_fields: CustomFieldChanges;
    }>
  ): Promise<ApiResponse<Deal>> {
    return api.put(`/deals/${dealId}`, data);
  },

  // Delete deal
  async deleteDeal(dealId: string): Promise<ApiResponse<{ message: string; dealId: string }>> {
    return api.delete(`/deals/${dealId}`);
  },

  // Move deal to different stage
  async moveDeal(dealId: string, stageId: string): Promise<ApiResponse<Deal>> {
    return api.put(`/deals/${dealId}/stage`, { stageId });
  },

  // Get deal stage history
  async getDealHistory(dealId: string): Promise<
    ApiResponse<{
      dealId: string;
      history: Array<{
        id: string;
        from_stage_id: string | null;
        from_stage_name: string | null;
        to_stage_id: string;
        to_stage_name: string;
        moved_at: number;
        moved_by: string | null;
      }>;
      count: number;
    }>
  > {
    return api.get(`/deals/${dealId}/history`);
  },

  // Get deals board for Kanban view
  async getDealsBoard(
    pipelineId: string,
    page = 1,
    limit = 50
  ): Promise<
    ApiResponse<{
      pipeline: Pipeline;
      stages: Array<{
        stage: Stage;
        deals: Deal[];
        count: number;
        totalCount: number;
        totalValue: number;
        hasMore: boolean;
      }>;
      totalDeals: number;
      totalValue: number;
    }>
  > {
    const searchParams = new URLSearchParams();
    searchParams.set('page', page.toString());
    searchParams.set('limit', limit.toString());

    const queryString = searchParams.toString();
    const endpoint = `/deals/pipeline/${pipelineId}/board?${queryString}`;

    return api.get(endpoint);
  },
};
